import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password, confirm_password: confirm });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-sm">
        <Link to="/" className="mb-8 flex items-center gap-2"><img src="/brand/procdeal-logo.png" alt="ProcDeal" className="h-8 w-auto" /></Link>
        <div className="rounded-xl border bg-card p-8">
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Reset password</h1>
          <p className="mt-1 text-sm text-muted-foreground">Choose a new password for your account.</p>
          {done ? (
            <div data-testid="reset-success" className="mt-6 text-center">
              <CheckCircle2 className="mx-auto h-10 w-10 text-[#22C55E]" />
              <p className="mt-3 font-heading text-base font-semibold">Your password has been reset successfully.</p>
              <Button data-testid="reset-goto-signin" className="mt-5 w-full bg-slate-900 hover:bg-slate-800" onClick={() => navigate("/login")}>Sign In</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label htmlFor="rp-pw">New Password</Label>
                <Input id="rp-pw" data-testid="reset-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min. 8 characters" className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="rp-cpw">Confirm Password</Label>
                <Input id="rp-cpw" data-testid="reset-confirm-input" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Repeat password" className="mt-1.5" />
              </div>
              {error && <p data-testid="reset-error" role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</p>}
              <Button type="submit" data-testid="reset-submit" disabled={loading || !token} className="w-full bg-slate-900 hover:bg-slate-800">
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Reset Password
              </Button>
              {!token && <p className="text-xs text-red-600">This reset link is missing its token. Use the link from your reset email.</p>}
            </form>
          )}
          <Link to="/login" className="mt-6 block text-center text-sm font-medium text-blue-700 hover:underline">Back to Sign In</Link>
        </div>
      </div>
    </div>
  );
}
